/**
 * Auto-download for the CAI sidecar GUI.
 *
 * The GUI binary is never bundled in the npm package (keeps the CLI light) and
 * lives in a PUBLIC dist repo so this published CLI can fetch it while the
 * source repo stays private. When `cai gui` finds no local binary, it downloads
 * the platform-matching binary from the dist repo's latest release, verifies its
 * SHA-256 against the release's `checksums.txt`, caches it under
 * `~/.cai/gui/<hash>/`, and returns the resolved binary path.
 *
 * Downloads go through GitHub's `releases/latest/download/<asset>` redirect —
 * NOT the api.github.com REST endpoint — so it never hits the unauthenticated
 * API rate limit (a 403 on shared/corporate IPs).
 *
 * All network and filesystem effects are injected so the logic is unit-testable
 * without hitting GitHub or disk.
 */

import { createHash } from "node:crypto";
import { existsSync, mkdirSync, writeFileSync, chmodSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";

/** Public dist repo that holds GUI release binaries (no source). */
export const DIST_REPO = "t3mr0i/cai-sidecar-dist";

/** Stable, platform-tagged asset base names produced by the release workflow. */
export function assetLabel(
  platform: NodeJS.Platform = process.platform,
  arch: string = process.arch
): string | null {
  if (platform === "darwin") return arch === "arm64" ? "macos-arm64" : "macos-x64";
  if (platform === "win32") return "windows-x64";
  return null; // linux GUI not distributed yet
}

/** Runnable asset file name for this platform (matches the release workflow). */
export function assetFileName(
  platform: NodeJS.Platform = process.platform,
  arch: string = process.arch
): string | null {
  const label = assetLabel(platform, arch);
  if (!label) return null;
  return `cai-sidecar-${label}.${platform === "win32" ? "exe" : "bin"}`;
}

export interface DownloadDeps {
  /** Fetch raw bytes (asset + checksums). Follows redirects. */
  fetchBytes?: (url: string) => Promise<Buffer>;
  /** Cache root; defaults to ~/.cai/gui. */
  cacheRoot?: string;
  platform?: NodeJS.Platform;
  arch?: string;
}

const defaultFetchBytes = async (url: string): Promise<Buffer> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed ${res.status} for ${url}`);
  return Buffer.from(await res.arrayBuffer());
};

export function sha256(buf: Buffer): string {
  return createHash("sha256").update(buf).digest("hex");
}

/** Parse a `sha256sum`-style checksums.txt into { filename: hash }. */
export function parseChecksums(text: string): Record<string, string> {
  const map: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const m = line.trim().match(/^([0-9a-f]{64})\s+\*?(.+)$/i);
    if (m) map[m[2]] = m[1].toLowerCase();
  }
  return map;
}

/**
 * Ensure the GUI is available locally, downloading + verifying it if needed.
 * Returns the path to the cached asset for this platform.
 *
 * Throws if the platform is unsupported, the release/asset is missing, or the
 * checksum does not match (never executes an unverified binary).
 */
export async function ensureGuiDownloaded(deps: DownloadDeps = {}): Promise<string> {
  const platform = deps.platform ?? process.platform;
  const arch = deps.arch ?? process.arch;
  const filename = assetFileName(platform, arch);
  if (!filename) {
    throw new Error(
      `No prebuilt CAI sidecar for ${platform}/${arch}. Build it from the gui/ workspace instead.`
    );
  }

  const fetchBytes = deps.fetchBytes ?? defaultFetchBytes;
  const cacheRoot = deps.cacheRoot ?? join(homedir(), ".cai", "gui");

  // GitHub redirects `releases/latest/download/<name>` to the asset on the latest
  // release — no api.github.com call, so no unauthenticated rate-limit 403.
  const base = `https://github.com/${DIST_REPO}/releases/latest/download`;

  const checksumText = (await fetchBytes(`${base}/checksums.txt`)).toString("utf8");
  const expected = parseChecksums(checksumText)[filename];
  if (!expected) {
    throw new Error(
      `The latest CAI sidecar release has no ${filename}. ` +
        `It may still be building, or this platform isn't published yet.`
    );
  }

  // Cache by content hash, so a new release (new hash) re-downloads automatically.
  const cacheDir = join(cacheRoot, expected.slice(0, 16));
  const dest = join(cacheDir, filename);
  if (existsSync(dest)) return dest;

  const bytes = await fetchBytes(`${base}/${filename}`);
  const actual = sha256(bytes);
  if (actual !== expected) {
    throw new Error(
      `Checksum mismatch for ${filename}: expected ${expected}, got ${actual}. Refusing to run.`
    );
  }

  mkdirSync(cacheDir, { recursive: true });
  writeFileSync(dest, bytes);
  if (platform !== "win32") chmodSync(dest, 0o755);
  return dest;
}
